export type LeaveType =
  | "PTO"
  | "SICK"
  | "PERSONAL"
  | "OTHER"
  // Legacy / pre-existing values — kept on the type so historical
  // data still validates. The request modal hides them; admins can
  // still assign them via the API and they roll up in reports.
  | "BEREAVEMENT"
  | "FMLA"
  | "UNPAID"
  | "JURY_DUTY"
  | "VOLUNTEER"
  | "CAREGIVING"
  | "MENTAL_HEALTH";

export type LeaveRequestStatus = "PENDING" | "APPROVED" | "DENIED" | "CANCELLED";

export interface LeaveBalance {
  id: string;
  employee_id: string;
  leave_type: LeaveType;
  total_days: number;
  used_days: number;
  pending_days: number;
  carry_over: number;
  remaining_days: number;
  year: number;
}

export interface LeaveRequest {
  id: string;
  org_id: string;
  employee_id: string;
  leave_type: LeaveType;
  /** When set, the request was filed against an org-defined custom
   *  leave type. The SPA renders ``custom_leave_type_name`` instead
   *  of the standard label. */
  custom_leave_type_id?: string | null;
  custom_leave_type_name?: string | null;
  start_date: string;
  end_date: string;
  total_days: number;
  status: LeaveRequestStatus;
  /** Free-text "why" the employee provided. Required for OTHER and
   *  custom types with ``requires_reason=true``. */
  reason?: string | null;
  notes: string | null;
  reviewer_notes: string | null;
  reviewed_at: string | null;
  /** HCP-cert expiry for protected leaves (FMLA/CFRA/ADA). NULL
   *  for non-protected leaves and for protected leaves where HR
   *  hasn't recorded a real date yet. Sentinel reads this to drive
   *  the leave_certification_expiring bucket; the TimeOff page
   *  renders an inline date picker for HR admins on protected
   *  leave rows. */
  certification_expires_at: string | null;
  created_at: string;
  employee_name?: string | null;
}

export interface LeaveRequestCreateData {
  leave_type: LeaveType;
  custom_leave_type_id?: string | null;
  start_date: string;
  end_date: string;
  reason?: string;
  notes?: string;
}

/**
 * Categories shown in the standard request dropdown. Keep this list
 * tight — research showed the old 10-option menu was confusing for
 * employees, who almost always wanted Vacation / Personal / Sick /
 * Other and used "Other" as the catch-all. Companies can still
 * extend the menu with their own ``CustomLeaveType`` rows.
 */
export const STANDARD_LEAVE_TYPES = [
  "PTO",
  "PERSONAL",
  "SICK",
  "OTHER",
] as const satisfies readonly LeaveType[];

export const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  // ``PTO`` is the canonical bucket name in the DB. Users see
  // "Vacation" — the friendlier label that matches every other HR
  // platform's request UI.
  PTO: "Vacation",
  SICK: "Sick",
  PERSONAL: "Personal",
  OTHER: "Other",
  BEREAVEMENT: "Bereavement",
  FMLA: "FMLA",
  UNPAID: "Unpaid",
  JURY_DUTY: "Jury Duty",
  VOLUNTEER: "Volunteer",
  CAREGIVING: "Caregiving",
  MENTAL_HEALTH: "Mental Health",
};

export const LEAVE_STATUS_COLORS: Record<LeaveRequestStatus, string> = {
  PENDING: "warning",
  APPROVED: "success",
  DENIED: "danger",
  CANCELLED: "default",
};

/**
 * Org-defined leave type — extends the standard menu. Admins manage
 * these from /settings (HR Admin role). Inactive types stay in the
 * DB so historical references on ``LeaveRequest`` still resolve.
 */
export interface CustomLeaveType {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  requires_reason: boolean;
  rollup_leave_type: LeaveType;
  default_annual_days: number;
  color: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CustomLeaveTypeInput {
  name: string;
  description?: string | null;
  requires_reason?: boolean;
  rollup_leave_type?: LeaveType;
  default_annual_days?: number;
  color?: string | null;
  is_active?: boolean;
  sort_order?: number;
}
